import React, { useEffect, useRef, useState } from 'react';
import { States } from 'constants/states';
import { IRootState } from 'constants/types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AudioStatuses } from 'redux/reducers/audioReducer';
import styled from 'styled-components/macro';
import Inner from 'components/Inner/Inner';
import { getAuthorsLabel } from 'utils/books';
import { ReactComponent as PlayIcon } from 'media/icons/play.svg';
import { ReactComponent as PauseIcon } from 'media/icons/pause.svg';
import { ReactComponent as PlayNextIcon } from 'media/icons/play-next.svg';
import { ReactComponent as PlayPrevIcon } from 'media/icons/play-prev.svg';
import { ReactComponent as ListIcon } from 'media/icons/list.svg';
import { ReactComponent as SpeedIcon } from 'media/icons/speed.svg';
import { ReactComponent as AngleIcon } from 'media/icons/angle-bottom.svg';
import { setAudioCurrentIndex, setAudioCurrentTime, setAudioPlaybackRate } from 'redux/actions/audio/audioActions';
import Breakpoints from 'themes/constants/breakpoints';
import { useMediaQuery } from 'react-responsive';
import placeholder from 'media/images/placeholder.jpg';
import useTranslate from 'hooks/useTranslate';
import { hexToRGBA } from 'utils/styles';
import { vsprintf } from 'sprintf-js';
import AudioProgressBase from 'components/Audio/progress/AudioProgress';
import RoutePath from 'routes/constants/routes';
import { booksAudioSpeed } from 'constants/books';
import Loader from 'components/Loader/Loader';
import useAudio from 'hooks/useAudio';

const AudioContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  flex-shrink: 0;
  height: 63px;
  border-top: ${({ theme }) => `1px solid ${hexToRGBA(theme.palette.greyMiddle, 0.5)}`};
  background: ${({ theme }) => theme.background.primary};
  z-index: 10000;
  @media only screen and (${Breakpoints.LAPTOP}) {
    height: 61px;
  }
`;
const AudioInner = styled(Inner)`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  height: 100%;
`;
const AudioPreview = styled.img.attrs({ alt: '' })`
  background: ${({ theme }) => theme.background.primary};
  width: 60px;
  height: 60px;
  border-radius: 0;
  object-fit: cover;
`;
const AudioCaption = styled(Inner)`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  padding-top: 4px;
  @media only screen and (${Breakpoints.LAPTOP}) {
    padding: ${({ theme }) => `0 ${theme.paddings.inner.desktop.paddingLeft} 0 12px`};
  }
`;
const AudioProgress = styled(AudioProgressBase)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  @media only screen and (${Breakpoints.LAPTOP}) {
    left: 0;
    right: 0;
  }
  @media only screen and (${Breakpoints.LAPTOP}) {
    left: 12px;
    right: ${({ theme }) => theme.paddings.inner.desktop.paddingLeft};
  }
`;
const AudioLabels = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  font-size: 16px;
  line-height: 1.2;
  flex-grow: 1;
  flex-shrink: 1;
  overflow: hidden;
  padding-right: 10px;
  cursor: pointer;
  & * {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const AudioLabelsAuthors = styled.div`
  color: ${({ theme }) => theme.palette.greyDark};
`;
const AudioLabelsName = styled.div`
  color: ${({ theme }) => theme.palette.black};
`;
const AudioControls = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-shrink: 0;
  margin-left: auto;
  gap: 12px;
`;
const AudioControlsButton = styled.button.attrs({ type: 'button' })<{ isActive?: boolean, isRotate?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  min-width: 24px;
  color: ${({ theme, isActive }) => isActive && theme.palette.secondary};
  transform: ${({ isRotate }) => isRotate && 'rotate(180deg)'};
  & > svg {
    width: 24px;
    height: 24px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const AudioDropdowns = styled.div`
  position: absolute;
  right: 12px;
  bottom: calc(100% + 12px);
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-end;
  gap: 12px;
  max-height: 70vh;
  @media only screen and (${Breakpoints.LAPTOP}) {
    bottom: calc(100% + 8px);
  }
`;
const AudioDropdownsItem = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  padding: 12px 24px;
  overflow-y: auto;
  border-radius: 12px;
  background: ${({ theme }) => theme.background.primary};
  box-shadow: ${({ theme }) => theme.boxShadow.wrap.main};
  z-index: 1000;
`;
const AudioDropdownsItemSpeed = styled(AudioDropdownsItem)`
  @media only screen and (${Breakpoints.LAPTOP}) {
    flex-flow: row nowrap;
    gap: 24px;
  }
`;
const AudioDropdownsItemButton = styled.div<{ isActive?: boolean }>`
  color: ${({ theme, isActive }) => (isActive ? theme.palette.secondary : theme.palette.greyDark)};
  text-align: center;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.palette.secondary};
  }
`;

interface IAudioProps {
  hide?: boolean
}

const Audio = (props: IAudioProps) => {
  const { hide = false } = props;

  const { handlePlay, handlePause } = useAudio();

  const dropButtonRef = useRef<HTMLButtonElement>(null);
  const headsButtonRef = useRef<HTMLButtonElement>(null);
  const speedButtonRef = useRef<HTMLButtonElement>(null);
  const headsDropRef = useRef<HTMLDivElement>(null);
  const speedDropRef = useRef<HTMLDivElement>(null);

  const [getText] = useTranslate();

  const isLaptop = useMediaQuery({ query: `(${Breakpoints.LAPTOP})` });

  const [isOpenHeads, setIsOpenHeads] = useState(false);
  const [isOpenSpeed, setIsOpenSpeed] = useState(false);

  const handleToggleHeads = () => setIsOpenHeads(!isOpenHeads);
  const handleCloseHeads = () => setIsOpenHeads(false);
  const handleToggleSpeed = () => setIsOpenSpeed(!isOpenSpeed);
  const handleCloseSpeed = () => setIsOpenSpeed(false);
  const handleToggleAll = () => {
    handleToggleHeads();
    handleToggleSpeed();
  };
  const handleCloseAll = () => {
    handleCloseHeads();
    handleCloseSpeed();
  };

  useEffect(() => {
    const listener = (event: any) => {
      if (isLaptop) {
        if (
          headsButtonRef?.current
          && headsButtonRef?.current.contains(event.target)
        ) {
          handleToggleHeads();
        } else if (
          headsButtonRef?.current
          && headsDropRef?.current
          && !headsButtonRef?.current.contains(event.target)
          && !headsDropRef?.current.contains(event.target)
        ) {
          handleCloseHeads();
        }

        if (
          speedButtonRef?.current
          && speedButtonRef?.current.contains(event.target)
        ) {
          handleToggleSpeed();
        } else if (
          speedButtonRef?.current
          && speedDropRef?.current
          && !speedButtonRef?.current.contains(event.target)
          && !speedDropRef?.current.contains(event.target)
        ) {
          handleCloseSpeed();
        }
      } else if (
        dropButtonRef?.current
          && dropButtonRef?.current.contains(event.target)
      ) {
        handleToggleAll();
      } else if (
        dropButtonRef?.current
        && headsDropRef?.current
        && speedDropRef?.current
        && !dropButtonRef?.current.contains(event.target)
        && !headsDropRef?.current.contains(event.target)
        && !speedDropRef?.current.contains(event.target)
      ) {
        handleCloseAll();
      }
    };

    window.addEventListener('click', listener);
    return () => window.removeEventListener('click', listener);
  }, [
    isLaptop,
    isOpenHeads,
    isOpenSpeed,
    dropButtonRef,
    headsButtonRef,
    speedButtonRef,
    headsDropRef,
    speedDropRef,
  ]);

  const history = useHistory();

  const dispatch = useDispatch();

  const audioState = useSelector((state: IRootState) => state?.[States.AUDIO]);
  const audioData = audioState?.data;
  const audioTime = audioState?.time;
  const audioStatus = audioState?.status;
  const audioLoading = audioState?.loading;

  const bookId = audioData?.id;
  const audioName = audioData?.name;
  const audioAuthors = audioData?.authors;
  const audioTimeCurrent = audioTime?.current || 0;
  const audioTimeTotal = audioTime?.total || 0;
  const audioList = audioData?.list || [];
  const audioCurrentIndex = audioState?.current?.index;
  const audioPreview = audioData?.preview || placeholder;
  const audioLength = Array.isArray(audioList) ? audioList.length : 0;
  const audioListHasOneMore = audioLength > 1;
  const audioPlaybackRate = audioTime?.playbackRate || 1;

  const isStatusPlay = audioStatus === AudioStatuses.PLAY;
  const isStatusStop = audioStatus === AudioStatuses.STOP;

  const showAudio = !hide && !isStatusStop && bookId;

  const handleOpen = () => history.push(vsprintf(RoutePath.BOOKS_DETAIL_PATH, [bookId]));
  const handleTogglePlay = () => {
    if (!isStatusPlay) {
      handlePlay();
    } else {
      handlePause();
    }
  };
  const handleSetIndex = (index: number) => dispatch(setAudioCurrentIndex(index));
  const handleBackAudioTime = () => {
    const newTime = (audioTimeCurrent <= 10) ? 0 : (audioTimeCurrent - 10);
    dispatch(setAudioCurrentTime(newTime));
  };
  const handleForwardAudioTime = () => {
    const newTime = ((audioTimeCurrent + 10) <= audioTimeTotal) ? (audioTimeCurrent + 10) : audioTimeTotal;
    dispatch(setAudioCurrentTime(newTime));
  };
  const handleSpeed = (num: number) => dispatch(setAudioPlaybackRate(num));

  const playIcon = (() => {
    if (audioLoading) return <Loader size={0.6} />;
    if (isStatusPlay) return <PauseIcon />;
    return <PlayIcon />;
  })();
  const controlsPlayerEl = (
    <>
      <AudioControlsButton
        onClick={handleBackAudioTime}
        disabled={audioLoading}
      >
        <PlayPrevIcon />
      </AudioControlsButton>
      <AudioControlsButton
        isActive={isStatusPlay}
        onClick={handleTogglePlay}
        disabled={audioLoading}
      >
        {playIcon}
      </AudioControlsButton>
      <AudioControlsButton
        onClick={handleForwardAudioTime}
        disabled={audioLoading}
      >
        <PlayNextIcon />
      </AudioControlsButton>
    </>
  );

  const controlsDropdownsEl = isLaptop ? (
    <>
      {audioListHasOneMore ? (
        <AudioControlsButton isActive={isOpenHeads} ref={headsButtonRef}>
          <ListIcon />
        </AudioControlsButton>
      ) : null}
      <AudioControlsButton isActive={isOpenSpeed} ref={speedButtonRef}>
        <SpeedIcon />
      </AudioControlsButton>
    </>
  ) : (
    <AudioControlsButton
      ref={dropButtonRef}
      isActive={isOpenHeads || isOpenSpeed}
      isRotate={isOpenHeads || isOpenSpeed}
    >
      <AngleIcon />
    </AudioControlsButton>
  );

  if (showAudio) {
    return (
      <AudioContainer>
        <AudioInner>
          {isLaptop ? (
            <AudioPreview
              src={audioPreview}
              decoding="async"
              onError={(e: any) => {
                e.target.onerror = null;
                e.target.src = placeholder;
              }}
            />
          ) : null}
          <AudioCaption withPaddings>
            <AudioProgress disabled={audioLoading} />
            <AudioLabels onClick={handleOpen}>
              <AudioLabelsAuthors>{getAuthorsLabel(audioAuthors)}</AudioLabelsAuthors>
              <AudioLabelsName>{audioName}</AudioLabelsName>
            </AudioLabels>
            <AudioControls>
              {controlsPlayerEl}
              {controlsDropdownsEl}
            </AudioControls>
          </AudioCaption>
          <AudioDropdowns>
            {isOpenHeads && audioListHasOneMore ? (
              <AudioDropdownsItem ref={headsDropRef}>
                {audioList?.map((item, index) => (
                  <AudioDropdownsItemButton
                    isActive={audioCurrentIndex === index}
                    onClick={() => handleSetIndex(index)}
                    key={item.abookFileId}
                  >
                    {vsprintf(getText(['words', 'headNumber']), [index + 1])}
                  </AudioDropdownsItemButton>
                ))}
              </AudioDropdownsItem>
            ) : null}
            {isOpenSpeed ? (
              <AudioDropdownsItemSpeed ref={speedDropRef}>
                {booksAudioSpeed?.map((item) => {
                  const { label, rate } = item;
                  return (
                    <AudioDropdownsItemButton
                      onClick={() => handleSpeed(rate)}
                      isActive={audioPlaybackRate === rate}
                      key={rate}
                    >
                      {getText(label)}
                    </AudioDropdownsItemButton>
                  );
                })}
              </AudioDropdownsItemSpeed>
            ) : null}
          </AudioDropdowns>
        </AudioInner>
      </AudioContainer>
    );
  }
  return null;
};

export default Audio;
